import type React from "react";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Button from "~/components/ui/button";
import { CustomCheckbox } from "~/components/ui/check_Box";
import DeleteIcon from "~/images/delete_row.svg?react";
import ItemSettingsModal from "../..";
import styles from "../../index.module.scss";

interface FieldInfo {
  code: string;
  label: string;
  type: string;
  options?: any;
}

interface SelectedField {
  type: string;
  code: string;
  label: string;
  noLabel?: boolean;
  required?: boolean;
  unique?: boolean;
  maxLength?: string;
  minLength?: string;
  defaultValue?: string;
  expression?: string;
  hideExpression?: boolean;
  [key: string]: any;
}

interface SubTableConfig {
  tableNoLabel?: boolean;
  fields?: {
    [fieldCode: string]: SelectedField;
  };
  [key: string]: any;
}

interface SubTableProps {
  options: SubTableConfig;
  onChange: (key: string, value: any) => void;
  getFieldsForApp: (appId: string) => FieldInfo[];
  currentAppId: string;
  isReadOnly?: boolean;
}

// フィールドタイプの選択肢（サブテーブル、関連レコード、ルックアップを除外）
const fieldTypeOptions = [
  { value: "SINGLE_LINE_TEXT", label: "文字列（1行）" },
  { value: "MULTI_LINE_TEXT", label: "文字列（複数行）" },
  { value: "NUMBER", label: "数値" },
  { value: "DATE", label: "日付" },
  { value: "LINK", label: "リンク" },
  { value: "CHECK_BOX", label: "チェックボックス" },
  { value: "RICH_TEXT", label: "リッチエディター" },
  { value: "FILE", label: "添付ファイル" },
  { value: "RADIO_BUTTON", label: "ラジオボタン" },
  { value: "MULTI_SELECT", label: "複数選択" },
  { value: "DROP_DOWN", label: "ドロップダウン" },
  { value: "GROUP", label: "グループ" },
  { value: "GROUP_SELECT", label: "グループ選択" },
  { value: "ORGANIZATION_SELECT", label: "組織選択" },
  { value: "USER_SELECT", label: "ユーザー選択" },
  { value: "DATETIME", label: "日時" },
  { value: "TIME", label: "時刻" },
];

const SubTableOptions: React.FC<SubTableProps> = ({
  options,
  onChange,
  getFieldsForApp: _getFieldsForApp,
  currentAppId,
  isReadOnly = false,
}) => {
  // サブテーブルに選択されているフィールド
  const [fields, setFields] = useState<Record<string, SelectedField>>(options.fields || {});

  // フィールドの並び順を管理するstate
  const [fieldOrder, setFieldOrder] = useState<string[]>(Object.keys(options.fields || {}));

  // テーブルラベル表示設定
  const [tableNoLabel, setTableNoLabel] = useState<boolean>(options["noLabel"] || false);

  // 詳細設定モーダル用のstate
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFieldForDetail, setSelectedFieldForDetail] = useState<string | null>(null);

  // optionsの変更を監視して状態を更新
  useEffect(() => {
    setFields(options.fields || {});
    setFieldOrder(Object.keys(options.fields || {}));
    setTableNoLabel(options["noLabel"] || false);
  }, [options]);

  // フィールド追加ハンドラー
  const handleAddField = () => {
    const newFieldCode = `field_${Date.now()}`;
    const newField: SelectedField = {
      type: "SINGLE_LINE_TEXT",
      code: newFieldCode,
      label: "新規フィールド",
      noLabel: false,
      required: false,
      options: {
        noLabel: false,
        required: false,
      },
    };

    const updatedFields = { ...fields, [newFieldCode]: newField };
    setFields(updatedFields);
    setFieldOrder([...fieldOrder, newFieldCode]);

    // 親コンポーネントに変更を通知
    onChange("fields", updatedFields);
  };

  // フィールド削除ハンドラー
  const handleRemoveField = (fieldCode: string) => {
    const updatedFields = { ...fields };
    delete updatedFields[fieldCode];
    const updatedOrder = fieldOrder.filter((code) => code !== fieldCode);

    setFields(updatedFields);
    setFieldOrder(updatedOrder);

    // 親コンポーネントに変更を通知
    onChange("fields", updatedFields);
  };

  // フィールド名の変更ハンドラー
  const handleFieldNameChange = (fieldCode: string, value: string) => {
    const updatedFields = {
      ...fields,
      [fieldCode]: {
        ...fields[fieldCode]!,
        label: value,
      },
    };
    setFields(updatedFields);
    onChange("fields", updatedFields);
  };

  // // フィールドコードの変更ハンドラー
  // const handleFieldCodeChange = (oldCode: string, newCode: string) => {
  //   if (fields[newCode] || !newCode) return;

  //   const updatedFields = { ...fields };
  //   updatedFields[newCode] = {
  //     ...updatedFields[oldCode],
  //     code: newCode
  //   };
  //   delete updatedFields[oldCode];

  //   setFields(updatedFields);
  //   setFieldOrder(fieldOrder.map(code => code === oldCode ? newCode : code));
  //   onChange('fields', updatedFields);
  // };

  // フィールドタイプの変更ハンドラー
  const handleFieldTypeChange = (fieldCode: string, type: string) => {
    const updatedFields = {
      ...fields,
      [fieldCode]: {
        ...fields[fieldCode]!,
        type: type,
        options: {
          noLabel: false,
          required: false,
        },
      },
    };
    setFields(updatedFields);
    onChange("fields", updatedFields);
  };

  // テーブルラベル表示設定の変更ハンドラー
  const handleTableNoLabelChange = (checked: boolean) => {
    setTableNoLabel(checked);
    onChange("noLabel", checked);
  };

  // 詳細設定の保存ハンドラー
  const handleDetailSave = (updatedOptions: any) => {
    if (!selectedFieldForDetail) return;

    const updatedFields = {
      ...fields,
      [selectedFieldForDetail]: {
        ...fields[selectedFieldForDetail]!,
        options: updatedOptions,
      },
    };

    setFields(updatedFields);
    onChange("fields", updatedFields);
    setShowDetailModal(false);
    setSelectedFieldForDetail(null);
  };

  return (
    <div className={styles["optionSection"]}>
      {/* テーブル設定 */}
      <div className={styles["checkboxOption"]}>
        <CustomCheckbox
          checked={tableNoLabel}
          onChange={handleTableNoLabelChange}
          label="テーブル名を表示しない"
          disabled={isReadOnly}
        />
      </div>

      {/* フィールド設定テーブル */}
      <div className={styles["subSection"]}>
        <div className={styles["sectionHeader"]}>
          <h4>サブテーブルのフィールド設定</h4>
          {!isReadOnly && (
            <Button onClick={handleAddField} label="フィールドを追加" styleType="blue" />
          )}
        </div>

        {/* フィールドテーブル */}
        <div className={styles["fieldTable"]}>
          <table>
            <thead>
              <tr>
                <th>フィールド名</th>
                <th>フィールドコード</th>
                <th>タイプ</th>
                <th>詳細設定</th>
                {!isReadOnly && <th></th>}
              </tr>
            </thead>
            <tbody>
              {fieldOrder.map((fieldCode) => {
                const field = fields[fieldCode];
                if (!field) return null;

                const fieldType = fieldTypeOptions.find((option) => option.value === field.type);
                const fieldTypeLabel = fieldType ? fieldType.label : "不明なタイプ";

                return (
                  <tr key={fieldCode}>
                    <td>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => handleFieldNameChange(fieldCode, e.target.value)}
                        disabled={isReadOnly}
                        className={styles["fieldInput"]}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={field.code}
                        disabled
                        className={styles["fieldInput"]}
                      />
                    </td>
                    <td>
                      {isReadOnly ? (
                        <span>{fieldTypeLabel}</span>
                      ) : (
                        <select
                          value={field.type}
                          onChange={(e) => handleFieldTypeChange(fieldCode, e.target.value)}
                          className={styles["fieldSelect"]}
                        >
                          {fieldTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      <Button
                        onClick={() => {
                          setSelectedFieldForDetail(fieldCode);
                          setShowDetailModal(true);
                        }}
                        label="設定"
                        styleType="link"
                      />
                    </td>
                    {!isReadOnly && (
                      <td>
                        <button
                          onClick={() => handleRemoveField(fieldCode)}
                          className={styles["removeButton"]}
                        >
                          <DeleteIcon width={16} height={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 詳細設定モーダル */}
      {showDetailModal && selectedFieldForDetail && fields[selectedFieldForDetail] && (
        <ItemSettingsModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          fieldData={{
            id: uuidv4(),
            app_id: currentAppId,
            フィールド名: fields[selectedFieldForDetail]!.label,
            フィールドコード: fields[selectedFieldForDetail]!.code,
            フィールドタイプ: fields[selectedFieldForDetail]!.type,
            options: fields[selectedFieldForDetail]!["options"] || {},
          }}
          onSave={handleDetailSave}
          isEditable={!isReadOnly}
          fieldTableData={Object.values(fields).map((field) => ({
            id: uuidv4(),
            app_id: currentAppId,
            フィールド名: field.label,
            フィールドコード: field.code,
            フィールドタイプ: field.type,
            options: field["options"] || {},
          }))}
        />
      )}
    </div>
  );
};

export default SubTableOptions;
